import type { ReactNode } from "react";
import clsx from "clsx";

export interface SegmentedOption<T extends string> { value: T; label: ReactNode; description?: ReactNode; disabled?: boolean }

/**
 * One selected value. Two looks: `pill` (compact switch, e.g. Me / Someone in my team, or Table / JSON) and
 * `cards` (radio-style option cards with a description). Defaults to cards when any option has a description.
 */
export function Segmented<T extends string>({ options, value, onChange, disabled, className, variant, size = "sm", ariaLabel }: { options: SegmentedOption<T>[]; value: T; onChange: (v: T) => void; disabled?: boolean; className?: string; variant?: "pill" | "cards"; size?: "sm" | "md" | "touch"; ariaLabel?: string }) {
  const v = variant ?? (options.some((o) => o.description) ? "cards" : "pill");
  if (v === "pill") {
    return (
      <div className={clsx("inline-flex rounded-[9px] border border-line bg-surface-2 p-[3px]", className)} role="radiogroup" aria-label={ariaLabel}>
        {options.map((o) => {
          const active = o.value === value;
          return (
            <button key={o.value} type="button" role="radio" aria-checked={active} disabled={disabled || o.disabled} onClick={() => onChange(o.value)}
              className={clsx("inline-flex items-center justify-center whitespace-nowrap rounded-[7px] px-3 font-semibold transition-colors disabled:opacity-45",
                size === "sm" ? "min-h-[26px] text-[13px]" : size === "md" ? "min-h-[34px]" : "min-h-[38px]",
                active ? "bg-surface text-fg shadow-e1" : "text-fg-2 hover:text-fg")}>
              {o.label}
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <div className={clsx("grid gap-2", className)} role="radiogroup" aria-label={ariaLabel}>
      {options.map((o) => {
        const active = o.value === value;
        const off = disabled || o.disabled;
        return (
          <button key={o.value} type="button" role="radio" aria-checked={active} disabled={off} onClick={() => onChange(o.value)}
            className={clsx("flex items-start gap-3 rounded-control border px-3 py-2.5 text-left transition-colors",
              active ? "border-brand bg-brand-soft" : "border-line hover:border-line-strong",
              off && "cursor-not-allowed opacity-60")}>
            <span aria-hidden="true" className={clsx("mt-1 h-3.5 w-3.5 shrink-0 rounded-full border-2", active ? "border-brand bg-brand" : "border-line-strong")} />
            <span className="min-w-0">
              <span className="block font-semibold">{o.label}</span>
              {o.description && <span className="block text-small text-fg-3">{o.description}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}
